/**
 * useWithMenuItem — dokleja do adresu nawigacji kontekst pozycji menu, z której user przyszedł
 * na listę: `?documentTypeIds=<CSV>` (zawężenie listy dokumentów do typów dokumentu) oraz
 * `mi=<menuItem.id>` (kartoteka-layout-per-menu). Kolejność parametrów jak w menu
 * (`Sidebar.tsx`): najpierw `documentTypeIds`, potem `mi`.
 *
 * Działa w OBU kierunkach — inaczej kontekst przepada na pierwszej nawigacji:
 *  - lista → karta (wiersz, „Nowy"): URL karty jest osobną nawigacją i nie dziedziczy query
 *    stringa listy, więc bez tego `useSystemModuleActiveLayout` na karcie nie ma skąd wziąć
 *    wskazanego układu;
 *  - karta → lista („Wstecz"/powrót w nagłówku, po usunięciu/archiwizacji, przekierowanie po
 *    utworzeniu): goły adres listy gubi układ przypisany do pozycji menu, zawężenie do typów
 *    dokumentu, a pasek kart spada do domyślnej nazwy karty. Hook w karcie czyta kontekst z
 *    adresu karty, na który został przeniesiony z listy.
 *
 * Argument `path` MUSI być pełną ścieżką (zaczyna się od `/`) — ścieżka względna (`'new'`,
 * `'contractors/1'`) wraca bez kontekstu, bo jej rodziny listy nie da się wyznaczyć. Doklejanie
 * parametrów zakłada ścieżkę bez fragmentu `#…` (żaden wołający go nie przekazuje; z fragmentem
 * parametry trafiłyby za `#`, czyli w nieistniejący query string).
 *
 * Reguła rodziny listy — kontekst dotyczy WYŁĄCZNIE tej listy, z której pochodzi. „Rodzina
 * listy" ścieżki to jej pierwszy segment (`/contracts/123` → `contracts`), a dla
 * `/modules/<slug>/...` dwa segmenty (`modules/<slug>`). Hook porównuje rodzinę CELU (część
 * przekazanej ścieżki przed pierwszym `?` lub `#`) z rodziną bieżącego `pathname`; przy różnicy
 * zwraca ścieżkę bez zmian (bez `mi`, bez `documentTypeIds`). Dzięki temu adres karty jednej
 * listy nigdy nie przenosi swojego kontekstu na inną listę — np. okno wyboru kontrahenta
 * osadzone na karcie faktury (`/purchase-invoices/new?...&mi=<pozycja faktur>`) otwiera
 * `/contractors/<id>` bez pozycji menu faktur.
 *
 * `mi` — id aktywnej pozycji własnego układu menu (`useActiveMenuItem`, czyta `?mi=` z bieżącego
 * URL-a i szuka pozycji w aktywnym układzie). Brak aktywnej pozycji → parametr nie jest doklejany.
 *
 * `documentTypeIds` — przepisane 1:1 z BIEŻĄCEGO URL-a, jeśli niepuste (same białe znaki to
 * brak). Zawężenie do typów jest częścią tożsamości pozycji menu (tak liczy ją `menuIdentityKey`
 * w `Sidebar.tsx`: ścieżka + posortowane `documentTypeIds` + `mi`) — menu zainstalowanych modułów
 * niesie zawężenie wyłącznie tym parametrem (ich pozycje nie mają id, więc nie mają `mi`), a
 * pozycje własnego układu mogą nieść je przez `mi` (filtr stały). Dlatego do karty i z powrotem
 * idą OBA parametry.
 *
 * Stary parametr `?documentTypeId=` (liczba pojedyncza): dawniej menu dokumentów zainstalowanych
 * modułów linkowało nim zawężenie listy, a pasek kart pamięta adresy, więc karta listy
 * `/internal-docs?documentTypeId=<T>` może jeszcze żyć. Gdy bieżący adres jest KORZENIEM listy
 * (`pathname` = sama rodzina — nie karta `/internal-docs/<id>`, nie `/internal-docs/new`),
 * wartość starego parametru wchodzi do niesionego `documentTypeIds=<CSV>`: gdy `documentTypeIds`
 * jest puste — jako jedyny typ; gdy oba są niepuste — dopisana NA KOŃCU CSV, o ile nie ma jej już
 * wśród jego segmentów (porównanie po przycięciu białych znaków; kolejność zachowana, bez
 * duplikatów). Łączenie jest zgodne z listą (`useDocumentTypeNarrowing` łączy oba parametry) i z
 * tożsamością pozycji menu (`menuIdentityKey` też je łączy) — inaczej powrót na listę zawęziłby
 * ją do węższego zbioru niż ten, z którego user przyszedł. Od tej chwili kontekst listy idzie
 * nowym parametrem. Na kartach stary parametr NIE jest przenoszony:
 * `.../new?documentTypeId=<T>&companyId=<C>` oznacza typ TWORZONEGO dokumentu, a nie zawężenie
 * listy.
 *
 * Parametr już obecny w przekazanej ścieżce nie jest duplikowany ani nadpisywany. Brak obu
 * parametrów → zwraca ścieżkę bez zmian.
 *
 * Wydzielone z istniejącego wzorca w `ModuleRecordsPage.tsx` (`withMenuItem`, dotąd tylko dla
 * modułów własnych z Kreatora) — reużywane przez listy kartotek systemowych i ich karty.
 */

import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useActiveMenuItem } from '@/hooks/useActiveMenuItem';

const MENU_ITEM_PARAM = 'mi';
const DOCUMENT_TYPE_IDS_PARAM = 'documentTypeIds';
const LEGACY_DOCUMENT_TYPE_ID_PARAM = 'documentTypeId';

/** Prefiks tras modułów własnych — ich rodzina listy to dwa segmenty (`modules/<slug>`). */
const MODULES_SEGMENT = 'modules';

/**
 * „Rodzina listy" ścieżki (patrz JSDoc hooka): pierwszy segment, a dla `/modules/<slug>/...` dwa.
 * `isListRoot` — ścieżka to sama rodzina, bez dalszych segmentów (korzeń listy, nie jej karta).
 */
function listFamilySegments(pathname: string): { family: string; isListRoot: boolean } {
  const segments = pathname.split('/').filter((segment) => segment.length > 0);
  const familyLength = segments[0] === MODULES_SEGMENT ? 2 : 1;
  return {
    family: segments.slice(0, familyLength).join('/'),
    isListRoot: segments.length === familyLength,
  };
}

/** Wartość parametru albo `null`, gdy brak lub same białe znaki. */
function nonBlank(value: string | null): string | null {
  return value !== null && value.trim().length > 0 ? value : null;
}

/**
 * CSV `documentTypeIds` z dopisaną NA KOŃCU wartością starego parametru `documentTypeId` — o ile
 * nie ma jej już wśród segmentów CSV (porównanie po przycięciu białych znaków). Gdy jest, zwraca
 * CSV 1:1. Oba argumenty są niepuste (`nonBlank`).
 */
function mergeLegacyTypeId(idsCsv: string, legacyId: string): string {
  const segments = idsCsv
    .split(',')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  const legacy = legacyId.trim();
  return segments.includes(legacy) ? idsCsv : [...segments, legacy].join(',');
}

/** Dokleja `name=value` (URL-encodowane) do ścieżki, o ile ścieżka nie niesie już tego parametru. */
function appendParamIfAbsent(path: string, name: string, value: string): string {
  const queryStart = path.indexOf('?');
  if (queryStart !== -1 && new URLSearchParams(path.slice(queryStart + 1)).has(name)) {
    return path;
  }
  const sep = queryStart === -1 ? '?' : '&';
  return `${path}${sep}${name}=${encodeURIComponent(value)}`;
}

export function useWithMenuItem(): (path: string) => string {
  const activeMenuItem = useActiveMenuItem();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const menuItemId = activeMenuItem?.id ?? null;
  const { family: currentFamily, isListRoot } = listFamilySegments(pathname);
  const idsParam = nonBlank(searchParams.get(DOCUMENT_TYPE_IDS_PARAM));
  // Stary parametr liczy się tylko na korzeniu listy (na kartach `documentTypeId` = typ tworzonego dokumentu).
  const legacyParam = isListRoot ? nonBlank(searchParams.get(LEGACY_DOCUMENT_TYPE_ID_PARAM)) : null;
  const documentTypeIds =
    idsParam !== null && legacyParam !== null
      ? mergeLegacyTypeId(idsParam, legacyParam)
      : (idsParam ?? legacyParam);

  return useCallback(
    (path: string) => {
      if (!path.startsWith('/')) return path;
      const targetEnd = path.search(/[?#]/);
      const targetPath = targetEnd === -1 ? path : path.slice(0, targetEnd);
      if (listFamilySegments(targetPath).family !== currentFamily) return path;

      let result = path;
      if (documentTypeIds) {
        result = appendParamIfAbsent(result, DOCUMENT_TYPE_IDS_PARAM, documentTypeIds);
      }
      if (menuItemId) result = appendParamIfAbsent(result, MENU_ITEM_PARAM, menuItemId);
      return result;
    },
    [currentFamily, menuItemId, documentTypeIds],
  );
}
